/** Focusable elements that we set/remove tabindex="-1" on */
const FOCUSABLE_SELECTOR =
	'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function updateCarouselAccessibility(swiper) {
	if (!swiper || !swiper.slides) return;
	const total = swiper.slides.length;
	const activeIndex = swiper.activeIndex;
	const slidesPerView =
		typeof swiper.params?.slidesPerView === 'number'
			? swiper.params.slidesPerView
			: 4;
	const visibleCount = Math.min(slidesPerView, total);
	const visibleEnd = activeIndex + visibleCount;

	if (swiper.thumbs) {
		swiper.thumbs.swiper?.slides.forEach((slide, index) => {
			if (index == activeIndex) {
				slide.setAttribute('aria-current', 'true');
			} else {
				slide.removeAttribute('aria-current');
			}
		});
	}

	if (swiper.pagination) {
		if (swiper.pagination.bullets.length > 0) {
			swiper.pagination.bullets.forEach((bullet, index) => {
				bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
				if (index == activeIndex) {
					bullet.setAttribute('aria-current', 'true');
				} else {
					bullet.removeAttribute('aria-current');
				}
			});
		}
	}

	Array.from(swiper.slides).forEach((slide, index) => {
		slide.setAttribute('role', 'group');
		slide.setAttribute('aria-label', `slide ${index + 1} of ${total}`);
		if (index == swiper.activeIndex) {
			slide.setAttribute('aria-current', 'true');
		} else {
			slide.removeAttribute('aria-current');
		}
		// For desktop
		if (window.innerWidth > 768) {
			const isVisible = index >= activeIndex && index < visibleEnd;

			if (isVisible) {
				slide.removeAttribute('aria-hidden');
			} else {
				slide.setAttribute('aria-hidden', 'true');
				slide.setAttribute('tabindex', '-1');
				slide
					.querySelectorAll(FOCUSABLE_SELECTOR)
					.forEach((el) => el.setAttribute('tabindex', '-1'));
			}
		} else {
			// For mobile
			const halfNumOfTotalSlides = Math.floor(total / 2);
			const isVisible =
				index === activeIndex ||
				index === activeIndex + 1 ||
				(index >= activeIndex + halfNumOfTotalSlides &&
					index < activeIndex + halfNumOfTotalSlides + visibleCount);

			if (isVisible) {
				slide.removeAttribute('aria-hidden');
			} else {
				slide.setAttribute('aria-hidden', 'true');
				slide.setAttribute('tabindex', '-1');
				slide
					.querySelectorAll(FOCUSABLE_SELECTOR)
					.forEach((el) => el.setAttribute('tabindex', '-1'));
			}
		}
	});
}

function attachSwiperAccessibility(swiper) {
	if (!swiper || swiper._a11yAttached) return;
	swiper._a11yAttached = true;

	updateCarouselAccessibility(swiper);
	swiper.on('slideChange', () => updateCarouselAccessibility(swiper));
	swiper.on('resize', () => updateCarouselAccessibility(swiper));

	const el = swiper.el;
	if (
		el &&
		el.classList.contains('carousel--product-thumbnails') &&
		swiper.slides &&
		swiper.slides.length
	) {
		swiper.slides.forEach((slide) => {
			slide.addEventListener(
				'focus',
				function reapplyTabindex() {
					setTimeout(() => updateCarouselAccessibility(swiper), 0);
				},
				true,
			);
		});
	}
}

function initCarouselAccessibility() {
	const swiperElements = document.querySelectorAll('.swiper');
	if (!swiperElements.length) return;

	swiperElements.forEach((swiperElement, index) => {
		//swiperElement.setAttribute('role', 'region');
		//swiperElement.setAttribute('aria-label', index + 1 + ' carousel');
		if (swiperElement.swiper) {
			attachSwiperAccessibility(swiperElement.swiper);
		} else {
			waitForSwiper(swiperElement);
		}
	});
}

function waitForSwiper(swiperElement, attempts = 0) {
	const maxAttempts = 50;
	const interval = 500;

	if (swiperElement.swiper) {
		attachSwiperAccessibility(swiperElement.swiper);
		return;
	}

	if (attempts < maxAttempts) {
		setTimeout(() => waitForSwiper(swiperElement, attempts + 1), interval);
	}
}

/**
 * ADA: Keyboard-accessible zoom for PDP product images.
 * Enter/Space opens a full-screen overlay with the full-size image.
 */
var _productZoomOverlay = null;
var _productZoomRestoreFocus = null;
var _productZoomImages = [];
var _productZoomCurrentIndex = 0;

function getProductZoomUrl(img) {
	return (img && (img.dataset.zoom || img.currentSrc || img.src)) || '';
}

function showProductZoomFullScreen(zoomImg, allZoomImages, currentIndex) {
	if (!zoomImg) return;
	var url = getProductZoomUrl(zoomImg);
	if (!url) return;

	_productZoomRestoreFocus = document.activeElement;
	_productZoomImages = allZoomImages || [];
	_productZoomCurrentIndex =
		typeof currentIndex === 'number' ? currentIndex : 0;

	if (_productZoomOverlay) {
		_productZoomOverlay.querySelector('.product-zoom-overlay__img').src = url;
		_productZoomOverlay.classList.add('product-zoom-overlay--open');
		_productZoomOverlay.querySelector('.product-zoom-overlay__close').focus();
		return;
	}

	var overlay = document.createElement('div');
	overlay.className = 'product-zoom-overlay product-zoom-overlay--open';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	overlay.setAttribute('aria-label', 'Full size product image');

	overlay.innerHTML =
		'<button type="button" class="product-zoom-overlay__close" aria-label="Close">\u00D7</button>' +
		'<div class="product-zoom-overlay__backdrop" aria-hidden="true"></div>' +
		'<div class="product-zoom-overlay__content">' +
		'<img class="product-zoom-overlay__img" src="" alt="Product full size" decoding="async">' +
		'</div>';

	var style = document.createElement('style');
	style.textContent =
		'.product-zoom-overlay{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;visibility:hidden;opacity:0;transition:visibility .2s,opacity .2s;}' +
		'.product-zoom-overlay--open{visibility:visible;opacity:1;}' +
		'.product-zoom-overlay__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.85);cursor:pointer;}' +
		'.product-zoom-overlay__content{position:relative;max-width:100%;max-height:100%;display:flex;align-items:center;justify-content:center;padding:2rem;}' +
		'.product-zoom-overlay__img{max-width:100%;max-height:calc(100vh - 4rem);width:auto;height:auto;object-fit:contain;}' +
		'.product-zoom-overlay__close{position:absolute;top:1rem;right:1rem;z-index:10;width:2.5rem;height:2.5rem;padding:0;border:none;background:#fff;color:#111;font-size:1.75rem;line-height:1;cursor:pointer;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,.3);}' +
		'.product-zoom-overlay__close:hover,.product-zoom-overlay__close:focus{background:#eee;outline:2px solid #fff;}';
	document.head.appendChild(style);

	var imgEl = overlay.querySelector('.product-zoom-overlay__img');
	imgEl.src = url;

	var closeBtn = overlay.querySelector('.product-zoom-overlay__close');
	var backdrop = overlay.querySelector('.product-zoom-overlay__backdrop');

	function close() {
		overlay.classList.remove('product-zoom-overlay--open');
		if (_productZoomRestoreFocus && _productZoomRestoreFocus.focus)
			_productZoomRestoreFocus.focus();
		_productZoomRestoreFocus = null;
	}

	closeBtn.addEventListener('click', close);
	backdrop.addEventListener('click', close);

	overlay.addEventListener('keydown', function (e) {
		if (e.key === 'Escape') {
			e.preventDefault();
			close();
			return;
		}
		if (!_productZoomImages.length) return;
		if (e.key === 'ArrowLeft') {
			e.preventDefault();
			_productZoomCurrentIndex =
				(_productZoomCurrentIndex - 1 + _productZoomImages.length) %
				_productZoomImages.length;
			imgEl.src = getProductZoomUrl(
				_productZoomImages[_productZoomCurrentIndex],
			);
		}
		if (e.key === 'ArrowRight') {
			e.preventDefault();
			_productZoomCurrentIndex =
				(_productZoomCurrentIndex + 1) % _productZoomImages.length;
			imgEl.src = getProductZoomUrl(
				_productZoomImages[_productZoomCurrentIndex],
			);
		}
	});

	document.body.appendChild(overlay);
	_productZoomOverlay = overlay;
	closeBtn.focus();
}

function hideProductZoomFullScreen() {
	if (_productZoomOverlay) {
		_productZoomOverlay.classList.remove('product-zoom-overlay--open');
		if (_productZoomRestoreFocus && _productZoomRestoreFocus.focus)
			_productZoomRestoreFocus.focus();
		_productZoomRestoreFocus = null;
	}
}

function initProductImageZoomAccessibility() {
	var featureEl = document.querySelector('.product__feature-image');
	if (!featureEl) return;

	var zoomImagesList = [].slice.call(
		featureEl.querySelectorAll('img.js--image-zoom'),
	);

	document.addEventListener('keydown', function productZoomEscape(e) {
		if (e.key === 'Escape') hideProductZoomFullScreen();
	});

	var slides = featureEl.querySelectorAll('.swiper-slide');
	slides.forEach(function (slide, slideIndex) {
		var zoomImg = slide.querySelector('img.js--image-zoom');
		if (!zoomImg || slide.dataset.zoomA11ySlideDone) return;
		slide.dataset.zoomA11ySlideDone = 'true';

		slide.addEventListener('keydown', function (e) {
			if (e.key !== 'Enter' && e.key !== ' ') return;
			e.preventDefault();
			var idx = zoomImagesList.indexOf(zoomImg);
			showProductZoomFullScreen(
				zoomImg,
				zoomImagesList,
				idx >= 0 ? idx : slideIndex,
			);
		});
	});

	zoomImagesList.forEach(function (img, idx) {
		if (img.dataset.zoomA11yDone) return;
		img.dataset.zoomA11yDone = 'true';

		img.setAttribute('tabindex', '0');
		if (!img.getAttribute('aria-label')) {
			img.setAttribute(
				'aria-label',
				'Product image. Activate to view full size.',
			);
		}

		img.addEventListener('keydown', function (e) {
			if (e.key !== 'Enter' && e.key !== ' ') return;
			e.preventDefault();
			showProductZoomFullScreen(img, zoomImagesList, idx);
		});
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		initCarouselAccessibility();
		initProductImageZoomAccessibility();
	});
} else {
	initCarouselAccessibility();
	initProductImageZoomAccessibility();
}

/* Rebuy ADA Fix */
function handleRebuySearchAdaFix() {
	const rebuyQuickViewOverlay = document.querySelector(
		'#rebuy-quick-view-overlay',
	);

	// Visually hidden live region: announce result count when search results change
	let resultsCountEl = rebuyQuickViewOverlay.querySelector(
		'.results-count.sr-only',
	);
	if (!resultsCountEl) {
		resultsCountEl = document.createElement('div');
		resultsCountEl.className = 'results-count sr-only';
		resultsCountEl.setAttribute('aria-live', 'assertive');
		rebuyQuickViewOverlay.insertBefore(
			resultsCountEl,
			rebuyQuickViewOverlay.firstChild,
		);
	}
	setTimeout(() => {
		const collectionList = rebuyQuickViewOverlay.querySelectorAll(
			'.rebuy-quick-view__collection',
		);
		const productList = rebuyQuickViewOverlay.querySelectorAll(
			'.rebuy-quick-view__product',
		);
		const collectionCount = collectionList ? collectionList.length : 0;
		const productCount = productList ? productList.length : 0;
		const total = collectionCount + productCount;
		resultsCountEl.textContent =
			total === 0 ? 'No results available.' : total + ' results available.';
	}, 250);

	// Replace section title <p> with <h2> (Rebuy renders it as p.rebuy-quick-view__title)
	setTimeout(() => {
		const rebuyQuickViewTitles = rebuyQuickViewOverlay.querySelectorAll(
			'.rebuy-quick-view__title',
		);
		rebuyQuickViewTitles.forEach(function (el) {
			if (el.tagName !== 'P') return;
			const h2 = document.createElement('h2');
			h2.className = el.className;
			while (el.firstChild) {
				h2.appendChild(el.firstChild);
			}
			el.parentNode.replaceChild(h2, el);
		});
	}, 250);

	setTimeout(() => {
		const rebuyQuickViewCollectionList = rebuyQuickViewOverlay.querySelector(
			'.rebuy-quick-view__collection-list',
		);
		if (rebuyQuickViewCollectionList) {
			const seeMoreButton =
				rebuyQuickViewCollectionList.querySelector('.see-more');
			if (seeMoreButton) {
				seeMoreButton.addEventListener('click', function () {
					const firstCollection = rebuyQuickViewCollectionList.querySelector(
						'.rebuy-quick-view__collection',
					);
					if (firstCollection) {
						if (
							firstCollection.tagName === 'A' &&
							firstCollection.hasAttribute('href')
						) {
							firstCollection.focus();
						} else {
							if (!firstCollection.hasAttribute('tabindex')) {
								firstCollection.setAttribute('tabindex', '-1');
							}
							firstCollection.focus();
						}
					}
				});
			}
		}
	}, 250);

	setTimeout(() => {
		const rebuyQuicViewProductList = rebuyQuickViewOverlay.querySelectorAll(
			'.rebuy-quick-view__product',
		);
		rebuyQuicViewProductList.forEach(function (productCard) {
			productCard.setAttribute('role', 'group');
			productCard.setAttribute('aria-label', 'Product');
		});
	}, 250);

	// Add h3 inside product title link (for accessibility)
	setTimeout(() => {
		const rebuyProductTitles = rebuyQuickViewOverlay.querySelectorAll(
			'.rebuy-product-title',
		);
		rebuyProductTitles.forEach(function (anchor) {
			if (anchor.tagName !== 'A') return;
			const h3 = document.createElement('h3');
			while (anchor.firstChild) {
				h3.appendChild(anchor.firstChild);
			}
			anchor.appendChild(h3);
		});
	}, 250);

	// trap Focus on rebuy-quick-view-dropdown-search
	const rebuyQuicViewDropdownContainer = document.querySelector(
		'.rebuy-quick-view-dropdown__container',
	);
	if (rebuyQuicViewDropdownContainer) {
		setTimeout(() => {
			trapFocus(rebuyQuicViewDropdownContainer, rebuyQuicViewDropdownContainer);
		}, 250);
	}

	// Remove aria-label in rebuy-product-title
	setTimeout(() => {
		const rebuyProductTitles = document.querySelectorAll(
			'.rebuy-product-actions .rebuy-button',
		);
		rebuyProductTitles.forEach(function (el) {
			el.removeAttribute('aria-label');
		});
	}, 250);
}

const trapFocusHandlers = {};

function getFocusableElements(container, ...excludeSelectors) {
	const allElements = Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
		),
	);

	const isHidden = (el) => {
		if (!el) return false;

		if (el.getAttribute('aria-hidden') === 'true') return true;

		const style = window.getComputedStyle(el);
		if (style && style.display === 'none') return true;

		return el.parentElement ? isHidden(el.parentElement) : false;
	};

	return allElements.filter((el) => {
		if (
			excludeSelectors.length &&
			excludeSelectors.some((sel) => el.closest(sel))
		)
			return false;
		if (isHidden(el)) return false;
		return true;
	});
}
function trapFocus(container, elementToFocus = container, ...excludeSelectors) {
	const initialElements = getFocusableElements(container, ...excludeSelectors);
	if (initialElements.length === 0) return;

	removeTrapFocus();

	trapFocusHandlers.container = container;
	trapFocusHandlers.excludeSelectors = excludeSelectors;

	const getFocusBoundaries = () => {
		const currentContainer = trapFocusHandlers.container;
		if (!currentContainer) return null;

		const elements = getFocusableElements(
			currentContainer,
			...trapFocusHandlers.excludeSelectors,
		);
		if (elements.length === 0) return null;
		return {
			first: elements[0],
			last: elements[elements.length - 1],
			container: currentContainer,
		};
	};

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return;

		const boundaries = getFocusBoundaries();
		if (!boundaries) return;

		const { first, last, container: currentContainer } = boundaries;
		const activeElement = document.activeElement;

		if (!currentContainer.contains(activeElement)) {
			event.preventDefault();
			event.stopPropagation();
			first.focus();
			return;
		}

		if (event.shiftKey && activeElement === first) {
			event.preventDefault();
			event.stopPropagation();
			last.focus();
			return;
		}

		if (!event.shiftKey && activeElement === last) {
			event.preventDefault();
			event.stopPropagation();
			first.focus();
		}
	};

	trapFocusHandlers.focusin = (event) => {
		const boundaries = getFocusBoundaries();
		if (!boundaries) return;

		const { first, container: currentContainer } = boundaries;

		if (!currentContainer.contains(event.target)) {
			event.preventDefault();
			event.stopPropagation();
			first.focus();
		}
	};

	document.addEventListener('keydown', trapFocusHandlers.keydown, true);
	document.addEventListener('focusin', trapFocusHandlers.focusin, true);

	elementToFocus.focus();

	if (
		elementToFocus.tagName === 'INPUT' &&
		['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
		elementToFocus.value
	) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length);
	}
}
function removeTrapFocus(elementToFocus = null) {
	if (trapFocusHandlers.focusin) {
		document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
	}
	if (trapFocusHandlers.focusout) {
		document.removeEventListener('focusout', trapFocusHandlers.focusout);
	}
	if (trapFocusHandlers.keydown) {
		document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
	}

	if (elementToFocus) elementToFocus.focus();
}

function setRebuyDropdownTopFromAnnouncementBar() {
	const rebuyQuicViewDropdownContainer = document.querySelector(
		'.rebuy-quick-view-dropdown__container',
	);
	const announcementBar = document.querySelector('.announcement-bar');
	const topPx = announcementBar ? announcementBar.offsetHeight : 0;
	if (rebuyQuicViewDropdownContainer) {
		rebuyQuicViewDropdownContainer.style.setProperty(
			'top',
			topPx + 'px',
			'important',
		);
	}
}

document.addEventListener('rebuy.ready', (event) => {
	setTimeout(() => {
		const rebuyQuicViewDropdownContainer = document.querySelector(
			'.rebuy-quick-view-dropdown__container',
		);
		if (rebuyQuicViewDropdownContainer) {
			rebuyQuicViewDropdownContainer.setAttribute('role', 'dialog');
			rebuyQuicViewDropdownContainer.setAttribute('aria-modal', 'true');
			rebuyQuicViewDropdownContainer.setAttribute(
				'aria-label',
				'Quick View Search',
			);
			setRebuyDropdownTopFromAnnouncementBar();
		}

		// Add screen reader only instructions before the live filter input
		const rebuyQuickViewSearchInput =
			rebuyQuicViewDropdownContainer &&
			rebuyQuicViewDropdownContainer.querySelector('#searchInput');
		if (rebuyQuickViewSearchInput) {
			const screenreaderText = document.createElement('span');
			screenreaderText.className = 'sr-only';
			screenreaderText.id = 'rebuy-quick-view-search-instructions';
			screenreaderText.textContent =
				'The following text field filters the results that follow as you type.';
			rebuyQuickViewSearchInput.parentNode.insertBefore(
				screenreaderText,
				rebuyQuickViewSearchInput,
			);
			rebuyQuickViewSearchInput.setAttribute(
				'aria-describedby',
				'rebuy-quick-view-search-instructions',
			);
		}

		// PDP YMAL product card ADA
		const rebuyProductCards = document.querySelectorAll('.rebuy-product-block');
		rebuyProductCards.forEach((productCard) => {
			productCard.setAttribute('role', 'group');
			const button = productCard.querySelector('.rebuy-button');
			if (button) {
				button.removeAttribute('aria-label');
			}
		});
	}, 500);
});

/* Rebuy Smart Cart - Accessibility Fix */
document.addEventListener('rebuy:smartcart.init', (event) => {
	setTimeout(() => {
		const rebuyCart = document.querySelector('#rebuy-cart');
		if (rebuyCart) {
			rebuyCart.classList.add('hide');
		}
	}, 500);
});
document.addEventListener('rebuy:smartcart.show', (event) => {
	setTimeout(() => {
		const rebuyCart = document.querySelector('#rebuy-cart');
		if (rebuyCart) {
			rebuyCart.classList.remove('hide');
		}
		const rebuyCartItems = rebuyCart.querySelectorAll(
			'.rebuy-cart__flyout-item',
		);

		rebuyCartItems.forEach((item) => {
			item.removeAttribute('aria-label');
			if (item.querySelector('.rebuy-cart__flyout-item')) return;

			const wrapper = document.createElement('div');
			wrapper.classList.add('rebuy-cart__flyout-item');
			wrapper.setAttribute('role', 'group');
			wrapper.setAttribute('aria-label', 'Cart item');

			while (item.firstChild) {
				wrapper.appendChild(item.firstChild);
			}
			item.appendChild(wrapper);
		});
	}, 500);
});
document.addEventListener('rebuy:smartcart.hide', (event) => {
	setTimeout(() => {
		const rebuyCart = document.querySelector('#rebuy-cart');
		if (rebuyCart) {
			rebuyCart.classList.add('hide');
		}
	}, 500);
});

/**
 * Move OneTrust cookie banner (#onetrust-consent-sdk) to sit directly below the "skip to main content" link.
 */
function moveOneTrustBannerBelowSkipLink() {
	const skipLink = document.querySelector(
		'.skip-link, a[href="#MainContent"].visually-hidden',
	);
	const banner = document.getElementById('onetrust-consent-sdk');
	if (!skipLink || !banner) return;
	if (skipLink.nextElementSibling === banner) return; // already in place

	skipLink.parentNode.insertBefore(banner, skipLink.nextSibling);
}

// OneTrust event: fires when the consent script loads
window.addEventListener('OneTrustGroupsUpdated', () => {
	setTimeout(() => {
		moveOneTrustBannerBelowSkipLink();
	}, 3000);
});

// Minimal accordion fix: override height when opened so content can expand
function initAccordionHeightFix() {
	const buttons = document.querySelectorAll('.js-accordion__button');
	if (!buttons.length) return;

	buttons.forEach((button) => {
		const contentId = button.getAttribute('aria-controls');
		if (!contentId) return;

		const content = document.getElementById(contentId);
		if (!content) return;

		button.addEventListener('click', () => {
			// Run after theme's own handler so we override its inline height
			setTimeout(() => {
				if (button.getAttribute('aria-expanded') === 'true') {
					content.removeAttribute('hidden');
					content.style.height = 'auto';
				}
			}, 0);
		});
	});
}

document.addEventListener('DOMContentLoaded', () => {
	// Fallback if our script loads after OneTrust (event may have already fired)
	setTimeout(() => {
		moveOneTrustBannerBelowSkipLink();
	}, 1000);

	const searchToggle = document.querySelector(
		'.menu__list .search-box__toggle',
	);
	if (searchToggle) {
		searchToggle.addEventListener('click', () => {
			setRebuyDropdownTopFromAnnouncementBar();
			// Rebuy may show the dropdown after a short delay; re-apply in case DOM updates
			setTimeout(setRebuyDropdownTopFromAnnouncementBar, 100);
		});
	}

	const contentPanel = document.querySelector('.section--content-panel');
	if (contentPanel) {
		contentPanel.setAttribute('role', 'group');
		contentPanel.setAttribute('aria-label', 'product highlight');

		const contentPanelText = contentPanel.querySelector('.content-panel__text');
		if (contentPanelText) {
			contentPanelText.setAttribute('role', 'group');
			contentPanelText.setAttribute('aria-label', 'customer review');
		}

		// Add screen reader only text for star rating in .content-panel__content .content-panel__text
		const contentPanelStars = contentPanel.querySelector(
			'.content-panel__text p:nth-child(2)',
		);
		if (contentPanelStars) {
			const visualStars = document.createElement('span');
			visualStars.setAttribute('aria-hidden', 'true');
			visualStars.textContent = contentPanelStars.textContent;

			const screenreaderText = document.createElement('span');
			screenreaderText.className = 'sr-only';
			screenreaderText.textContent = '5 out of 5 stars.';

			contentPanelStars.textContent = '';
			contentPanelStars.appendChild(visualStars);
			contentPanelStars.appendChild(screenreaderText);
		}
	}

	// Fix accordion height after theme script runs
	initAccordionHeightFix();
});

// Yotpo ADA Fix
// Listen for Yotpo Product Reviews to be added to the DOM
(function () {
	let yotpoFixed = false;

	function applyYotpoAdaFixes() {
		if (yotpoFixed) return;

		const yotpoPDPWidget = document.querySelector('#yotpo-app');
		if (!yotpoPDPWidget) return;

		const appblockParentOfYotpoPDPWidget = yotpoPDPWidget.closest(
			'.yotpo-widget-clear',
		);
		if (!appblockParentOfYotpoPDPWidget) return;

		appblockParentOfYotpoPDPWidget.setAttribute('role', 'region');
		yotpoFixed = true;
	}

	applyYotpoAdaFixes();

	// Watch for Yotpo elements being added
	const observer = new MutationObserver(function (mutations) {
		applyYotpoAdaFixes();
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Cleanup observer after 30 seconds to avoid performance impact
	setTimeout(function () {
		observer.disconnect();
	}, 30000);
})();

/* Mobile menu ADA Fix */
document.addEventListener('DOMContentLoaded', () => {
	const mobileMenuTrigger = document.querySelector('.header__link--menu');
	if (mobileMenuTrigger) {
		mobileMenuTrigger.addEventListener('click', () => {
			if (mobileMenuTrigger.classList.contains('js--is-open')) {
				removeTrapFocus();
			} else {
				const mobileHeaderContainer =
					document.querySelector('.header__container');
				// Exclude the logo, search toggle, and cart link from the trap focus
				trapFocus(
					mobileHeaderContainer,
					mobileMenuTrigger,
					'.header__link--logo',
					'.search-box__toggle',
					'a.header__link--cart',
				);
			}
		});
	}
});
